import i18n from "@/core/i18n";
import type { RootState } from "@/store";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Project, resolveFeatureFlags } from "@repo/schema";

export interface ProjectState {
  activeProject: Project | null;
}

const initialState: ProjectState = {
  activeProject: null,
};

export const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    setActiveProject: (state, { payload }: PayloadAction<Project | null>) => {
      sessionStorage.setItem("activeProjectId", JSON.stringify(payload?.id));
      state.activeProject = payload;
    },
  },
});

/**
 * Where the active project's camera is, and why it is not reachable when it
 * isn't.
 *
 * One discriminated union rather than a URL plus a scattering of booleans,
 * because the failure modes are what the UI actually has to distinguish and
 * they used to be indistinguishable: a relay handshake that failed permanently
 * and one still in flight both read as "not ready", so the Capture page sat on
 * "Searching for camera..." forever, and a project whose organization had local
 * access revoked was reported as "no camera configured" with nothing naming the
 * real cause.
 */
export type CameraApiStatus =
  /** Addressable now. `credentials` belongs to the URL — see below. */
  | { kind: "ready"; url: string; credentials: RequestCredentials }
  /** Relay-backed, cookie still being minted. Transient; wait. */
  | { kind: "connecting" }
  /** Relay-backed and the handshake failed. Not the user's to fix in settings. */
  | { kind: "relay-unavailable"; message: string }
  /** A local address is stored but the organization may no longer use one. */
  | { kind: "local-access-revoked"; cameraApiUrl: string }
  /** Neither a device nor an address. The one case settings can resolve. */
  | { kind: "unconfigured" };

/**
 * The single source for every camera API request — REST, the detections
 * WebSocket, the WebRTC SDP offer, `<img>` event pictures and the dashboard
 * `<iframe>`.
 *
 * A device and a local address are mutually exclusive targets, not a fallback
 * chain. `project.device` decides alone: falling through to `cameraApiUrl` when
 * the relay was not ready sent requests to a LAN address that a relay-backed
 * project has no reason to be able to reach — and did so *only* for
 * organizations with local access enabled, so enabling the flag broke remote
 * devices. The database says the same thing (`project.device_id` "takes
 * precedence over cameraApiUrl"); this is where it is finally true.
 */
export const selectCameraApiStatus = createSelector(
  [
    (state: RootState) => state.project.activeProject,
    (state: RootState) => state.relay.relayUrl,
    (state: RootState) => state.relay.status,
    (state: RootState) => state.relay.error,
    (state: RootState) => state.auth.organization?.featureFlags,
  ],
  (project, relayUrl, relayStatus, relayError, featureFlags): CameraApiStatus => {
    if (!project) {
      return { kind: "unconfigured" };
    }

    if (project.device) {
      // Gated on the cookie, not just on the URL: requests sent before it is
      // established would 401, and `<img>` and WebSocket give no opportunity to
      // retry with a credential.
      if (relayStatus === "ready" && relayUrl) {
        return {
          kind: "ready",
          url: `${relayUrl}/d/${project.device.deviceId}`,
          credentials: "include",
        };
      }

      return relayStatus === "error"
        ? {
            kind: "relay-unavailable",
            message: relayError ?? i18n.t("device:relay.unavailable"),
          }
        : { kind: "connecting" };
    }

    if (!project.cameraApiUrl) {
      return { kind: "unconfigured" };
    }

    // With the flag off the organization has no local path at all. Reporting it
    // as its own state is what lets the UI say so, instead of sending someone
    // to the settings modal to fix something that is not misconfigured.
    return resolveFeatureFlags(featureFlags).localCameraAccess
      ? { kind: "ready", url: project.cameraApiUrl, credentials: "same-origin" }
      : { kind: "local-access-revoked", cameraApiUrl: project.cameraApiUrl };
  },
);

/** The camera API base URL, or null when {@link selectCameraApiStatus} is not ready. */
export const selectCameraApiUrl = (state: RootState): string | null => {
  const status = selectCameraApiStatus(state);

  return status.kind === "ready" ? status.url : null;
};

/**
 * Credentials mode for a request to {@link selectCameraApiUrl}.
 *
 * Stated by the branch that built the URL rather than recovered from it
 * afterwards. The two answers are one decision and drifted apart once already:
 * raw `fetch` sites were taught to send the relay cookie unconditionally, which
 * broke local camera access outright. The relay authorizes by a cookie on its
 * own origin, so it needs one; a LAN device answers
 * `Access-Control-Allow-Origin: *` with no `Access-Control-Allow-Credentials`,
 * and a browser refuses that pairing for any credentialed request — the
 * preflight fails and the request never reaches the device.
 */
export const selectCameraApiCredentials = (
  state: RootState,
): RequestCredentials => {
  const status = selectCameraApiStatus(state);

  return status.kind === "ready" ? status.credentials : "same-origin";
};
